/**
 * AspectAwareImage - Clean car image component
 * Uses object-cover with center positioning for professional car display
 * Matches the detail page gallery look
 */
import { useState, useCallback } from 'react';
import { OptimizedImage } from './optimized-image';
import { ImagePlaceholder } from './ImagePlaceholder';
import { cn } from '@/lib/utils';

interface AspectAwareImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  grayscale?: boolean;
  onLoad?: () => void;
}

export function AspectAwareImage({
  src,
  alt,
  className = '',
  containerClassName = '',
  width = 400,
  height = 300,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
  priority = false,
  grayscale = false,
  onLoad,
}: AspectAwareImageProps) {
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  if (hasError || !src) {
    return (
      <div className={cn("aspect-[4/3] bg-muted", containerClassName)}>
        <ImagePlaceholder iconSize="lg" />
      </div>
    );
  }

  return (
    <div className={cn("relative aspect-[4/3] overflow-hidden", containerClassName)}>
      <OptimizedImage
        src={src}
        alt={alt}
        className={cn(
          "absolute inset-0 w-full h-full object-cover object-center bg-muted",
          grayscale && 'grayscale opacity-70',
          className
        )}
        width={width}
        height={height}
        sizes={sizes}
        priority={priority}
        onLoad={onLoad}
        onError={handleError}
      />
    </div>
  );
}

export default AspectAwareImage;
